import React, { useState } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  CircularProgress
} from '@mui/material';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ExecuteApiToPost } from '../../../../../../services/api/ExecuteApiRequests';

const RegisterConsultant = () => {
  const [name, setName] = useState("");
  const [classValue, setClassValue] = useState(1);
  const [licenseNo, setLicenseNo] = useState("");
  const [tinNo, setTinNo] = useState("");
  const [originCountry, setOriginCountry] = useState("Ethiopia");
  const [addressRegion, setAddressRegion] = useState("");
  const [addressCity, setAddressCity] = useState("");
  const [addressSubCity, setAddressSubCity] = useState("");
  const [addressWoreda, setAddressWoreda] = useState("");
  const [addressHouseNo, setAddressHouseNo] = useState("");
  const [contactPersonName, setContactPersonName] = useState("");
  const [telephone, setTelephone] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [consultantType, setConsultantType] = useState("");
  const [yearsOfExperience, setYearsOfExperience] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Consultant grades
  const grades = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  // Consultant types
  const consultantTypes = [
    "Architectural Consultant",
    "Structural Consultant",
    "Electrical Consultant",
    "Mechanical Consultant",
    "Sanitary Consultant",
    "Environmental Consultant",
	"ElectroMechnical Consultant",
	"General consultant",
    "Project Management Consultant",
    "Engineering Consultant"
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    setSubmitting(true);
    
    try {
      const responseData = await ExecuteApiToPost("/consultants", {
        name,
        cls: classValue,
        license_no: licenseNo,
        tin_no: tinNo,
        origin_country: originCountry,
        address_region: addressRegion,
        address_city: addressCity,
        address_subcity: addressSubCity,
        address_woreda: addressWoreda,
        address_house_no: addressHouseNo,
        contact_person_name: contactPersonName,
        telephone,
        email,
        description,
        consultant_type: consultantType,
        experience_year: yearsOfExperience,
      });
      
      console.log("Response:", responseData);
      
      if (responseData) {
        toast.success(`✅ Consultant registered successfully with ID: ${responseData[0]?.id || responseData.id}`, { 
          position: "top-right", 
          autoClose: 5000 
        });
        
        // Reset form fields
        setName("");
        setClassValue(1);
        setLicenseNo("");
        setTinNo("");
        setOriginCountry("Ethiopia");
        setAddressRegion("");
        setAddressCity("");
        setAddressSubCity("");
        setAddressWoreda("");
        setAddressHouseNo("");
        setContactPersonName("");
        setTelephone("");
        setEmail("");
        setDescription("");
        setConsultantType("");
        setYearsOfExperience("");
      }
    } catch (error) {
      console.error("Registration error:", error);
      
      let errorMessage = "❌ An error occurred. Please try again.";
      if (error.response?.data?.message) {
        errorMessage = `❌ ${error.response.data.message}`;
      } else if (error.message) {
        errorMessage = `❌ ${error.message}`;
      }
      
      toast.error(errorMessage, { 
        position: "top-right", 
        autoClose: 5000 
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: 3,
      fontFamily: '"Times New Roman", Times, serif'
    }}>
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          align="center"
          sx={{ 
            fontFamily: '"Times New Roman", Times, serif',
            fontWeight: 'bold',
            color: '#333',
            mb: 4
          }}
        >
          Consultant Registration
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Row 1: Company Name, Consultant Grade, License No */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Company Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                variant="outlined"
                size="small"
                disabled={submitting}
                InputProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" required>
                <InputLabel sx={{ fontFamily: '"Times New Roman", Times, serif' }}>
                  Consultant Grade
                </InputLabel>
                <Select
                  value={classValue}
                  onChange={(e) => setClassValue(e.target.value)}
                  label="Consultant Grade"
                  disabled={submitting}
                  sx={{ fontFamily: '"Times New Roman", Times, serif' }}
                >
                  {grades.map((grade) => (
                    <MenuItem 
                      key={grade} 
                      value={grade}
                      sx={{ fontFamily: '"Times New Roman", Times, serif' }}
                    >
                      Grade {grade}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="License No"
                value={licenseNo}
                onChange={(e) => setLicenseNo(e.target.value)}
                required
                variant="outlined"
                size="small"
                disabled={submitting}
                InputProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
              />
            </Grid>

            {/* Row 2: Consultant Type, Years of Experience, TIN No */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" required>
                <InputLabel sx={{ fontFamily: '"Times New Roman", Times, serif' }}>
                  Consultant Type
                </InputLabel>
                <Select
                  value={consultantType}
                  onChange={(e) => setConsultantType(e.target.value)}
                  label="Consultant Type"
                  disabled={submitting}
                  sx={{ fontFamily: '"Times New Roman", Times, serif' }}
                >
                  {consultantTypes.map((type) => (
                    <MenuItem 
                      key={type} 
                      value={type}
                      sx={{ fontFamily: '"Times New Roman", Times, serif' }}
                    >
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Years of Experience"
                type="number"
                value={yearsOfExperience}
                onChange={(e) => setYearsOfExperience(e.target.value)}
                required
                variant="outlined"
                size="small"
                disabled={submitting}
                InputProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' },
                  inputProps: { min: 0 }
                }}
                InputLabelProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="TIN No"
                value={tinNo}
                onChange={(e) => setTinNo(e.target.value)}
               
                variant="outlined"
                size="small"
                disabled={submitting}
                InputProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
              />
            </Grid>

            {/* Row 3: Origin Country, Telephone, Email */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Origin Country"
                value={originCountry}
                onChange={(e) => setOriginCountry(e.target.value)}
               
                variant="outlined"
                size="small"
                disabled={submitting}
                InputProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Telephone"
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                
                variant="outlined"
                size="small"
                disabled={submitting}
                InputProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                
                variant="outlined"
                size="small"
                disabled={submitting}
                InputProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
              />
            </Grid>

            {/* Row 4: Contact Person, Region, City */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Contact Person Name"
                value={contactPersonName}
                onChange={(e) => setContactPersonName(e.target.value)}
                required
                variant="outlined"
                size="small"
                disabled={submitting}
                InputProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Region"
                value={addressRegion}
                onChange={(e) => setAddressRegion(e.target.value)}
                
                variant="outlined"
                size="small"
                disabled={submitting}
                InputProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                value={addressCity}
                onChange={(e) => setAddressCity(e.target.value)}
                
                variant="outlined"
                size="small"
                disabled={submitting}
                InputProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
              />
            </Grid>

            {/* Row 5: Sub City, Woreda, House No */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Sub City"
                value={addressSubCity}
                onChange={(e) => setAddressSubCity(e.target.value)}
                
                variant="outlined"
                size="small"
                disabled={submitting}
                InputProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Woreda"
                value={addressWoreda}
                onChange={(e) => setAddressWoreda(e.target.value)}
              
                variant="outlined"
                size="small"
                disabled={submitting}
                InputProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="House No"
                value={addressHouseNo}
                onChange={(e) => setAddressHouseNo(e.target.value)}
                
                variant="outlined"
                size="small"
                disabled={submitting}
                InputProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
              />
            </Grid>

            {/* Row 6: Description - Full Width */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={4}
                variant="outlined"
                disabled={submitting}
                InputProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: '"Times New Roman", Times, serif' }
                }}
              />
            </Grid>

            {/* Loading Indicator */}
            {submitting && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 2 }}>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  <Typography variant="body2" sx={{ fontFamily: '"Times New Roman", Times, serif' }}>
                    Registering consultant...
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Row 7: Submit Button - Centered */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  sx={{
                    minWidth: '200px',
                    fontFamily: '"Times New Roman", Times, serif',
                    backgroundColor: '#d3d3d3',
                    color: '#333333',
                    border: '1px solid #a9a9a9',
                    '&:hover': {
                      backgroundColor: '#c0c0c0',
                    },
                    '&:disabled': {
                      backgroundColor: '#e0e0e0',
                      color: '#9e9e9e',
                    }
                  }}
                >
                  {submitting ? <CircularProgress size={24} color="inherit" /> : "Register Consultant"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default RegisterConsultant;